import type { SupabaseClient } from '@supabase/supabase-js';
import { showToast } from '@vario-connect/frontend-design';
import { loadMappingSources, type FlatSource } from './mapping-helpers.js';
import { clearTabDirty } from '../../lib/tab-dirty.js';

// ============================================================================
// Gambio-Lister - Tab „Zusatzfelder" (per-Artikel Mapping mit 4 Modi + Inline-Wert).
//
// Jedes Gambio-Zusatzfeld bekommt - wie die Stammdaten im Tab „Übersicht & Mapping" —
// einen Modus-Selektor:
//   Vorlage (global) = folgt dem globalen Zusatzfeld-Mapping (Startzustand, nicht gespeichert)
//   Vario-Feld       = Wert aus einem Vario-Pfad
//   Fester Wert      = die am Artikel gepflegten mehrsprachigen Werte (Vario-EAV f<id>_<lang>)
//   Nicht gemappt    = für diesen Artikel nicht übertragen
// Der Modus wird im geteilten per-Artikel-Override (gambio.listings.mapping_override,
// field_group='additional', key 'f<id>') gespeichert; die mehrsprachigen WERTE bleiben in
// Vario-EAV (via gambio-article-additional). Das Override wird mit dem Stammdaten-Tab GETEILT →
// beim Speichern werden dessen 'product_data_basic'-Zeilen erhalten.
// Zusatzfelder sind SHOP-global (nicht kategoriegebunden) → kein Kategorie-Gate.
// ============================================================================

export interface AdditionalFieldsCtx {
  supabase: SupabaseClient;
  supabaseUrl: string;
  session: { token: string };
}
export interface AdditionalFieldsArgs {
  storefront: string;
  /** Akzeptiert für Aufruf-Symmetrie, aber UNGENUTZT - Zusatzfelder sind shop-global. */
  gambioCategoryId?: string | null;
  varioArticleId: string;
  listingId: string;
}

interface SchemaField { key: string; group: string; label: string }
interface LangCell { lang: string; attrKey: string }
interface FieldRow { fieldId: number; baseLabel: string; cells: LangCell[] }
interface OverrideField { gambio_field_key: string; field_group: string; source_type: string; vario_path?: string | null; fixed_value?: unknown; template?: string | null }

function esc(s: string | null | undefined): string {
  if (s == null) return '';
  return String(s).replaceAll('&','&amp;').replaceAll('<','&lt;').replaceAll('>','&gt;').replaceAll('"','&quot;').replaceAll("'",'&#39;');
}

async function callFn<T>(ctx: AdditionalFieldsCtx, name: string, body: unknown): Promise<T> {
  const res = await fetch(`${ctx.supabaseUrl}/functions/v1/${name}`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${ctx.session.token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) { const t = await res.text().catch(() => ''); throw new Error(`${name} ${res.status}: ${t.slice(0, 200)}`); }
  return (await res.json()) as T;
}

/** 'additional:f12:de' → {fieldId:12, lang:'de'} ; Legacy 'additional:f12' → lang 'de' ; sonst null. */
function parseAdditionalKey(key: string): { fieldId: number; lang: string } | null {
  const m = key.match(/^additional:f(\d+)(?::([a-z]{2,}))?$/i);
  if (!m) return null;
  return { fieldId: Number(m[1]), lang: (m[2] ?? 'de').toLowerCase() };
}

/** Vario-EAV-Attributschlüssel (spiegelt eavAttrKey in apps/worker/src/lib/vario-article-eav.ts). */
const attrKey = (fieldId: number, lang: string): string => `f${fieldId}_${lang.toLowerCase()}`;

/** Entfernt das Sprach-Suffix „ (de)" aus dem Schema-Label → Basis-Label für die Feld-Überschrift. */
function baseLabelOf(label: string, lang: string): string {
  const stripped = label.replace(new RegExp('\\s*\\(' + lang + '\\)\\s*$', 'i'), '').trim();
  return stripped.length > 0 ? stripped : label;
}

/** de zuerst, dann alphabetisch. */
function sortLangs(a: string, b: string): number {
  if (a === b) return 0;
  if (a === 'de') return -1;
  if (b === 'de') return 1;
  return a.localeCompare(b);
}

function banner(kind: 'warn' | 'error' | 'info', msg: string): string {
  const cls = kind === 'error' ? 'v-banner-danger' : kind === 'warn' ? 'v-banner-warn' : 'v-banner-info';
  const icon = kind === 'error' ? 'fa-circle-xmark' : kind === 'warn' ? 'fa-triangle-exclamation' : 'fa-circle-info';
  return `<div class="v-pa-md"><div class="v-banner ${cls}"><span class="v-icon fa-light ${icon}"></span><div>${esc(msg)}</div></div></div>`;
}

const AF_MODES: Array<{ value: string; label: string }> = [
  { value: 'inherit',     label: 'Vorlage (global)' },
  { value: 'vario_path',  label: 'Vario-Feld' },
  { value: 'fixed_value', label: 'Fester Wert' },
  { value: 'unmapped',    label: 'Nicht gemappt' },
];

export async function renderListAdditionalFields(
  host: HTMLElement,
  ctx: AdditionalFieldsCtx,
  args: AdditionalFieldsArgs,
): Promise<void> {
  host.innerHTML = `<div class="klst-loading"><span class="v-icon fa-regular fa-spinner-third fa-spin"></span> Lade Zusatzfelder…</div>`;

  let rows: FieldRow[];
  let values: Record<string, string>;
  // Gruende aus gambio-listing-schema, falls der Zusatzfeld-Katalog nicht gelesen werden konnte.
  // Ohne sie ist „keine Zusatzfelder" zweideutig: Shop hat keine ODER Shop war nicht erreichbar.
  let schemaWarnings: string[] = [];
  try {
    // Zusatzfeld-Katalog ist shop-global - gambio-listing-schema ignoriert die Kategorie.
    const schema = await callFn<{ fields: SchemaField[]; warnings?: string[] }>(ctx, 'gambio-listing-schema', {
      storefront: args.storefront,
    });
    schemaWarnings = schema.warnings ?? [];
    const byField = new Map<number, FieldRow>();
    for (const f of schema.fields ?? []) {
      if (f.group !== 'additional') continue;
      const parsed = parseAdditionalKey(f.key);
      if (!parsed) continue;
      const { fieldId, lang } = parsed;
      let row = byField.get(fieldId);
      if (!row) { row = { fieldId, baseLabel: baseLabelOf(f.label, lang), cells: [] }; byField.set(fieldId, row); }
      if (!row.cells.some((c) => c.lang === lang)) row.cells.push({ lang, attrKey: attrKey(fieldId, lang) });
    }
    rows = [...byField.values()].sort((a, b) => a.fieldId - b.fieldId);
    for (const r of rows) r.cells.sort((a, b) => sortLangs(a.lang, b.lang));

    if (rows.length === 0) {
      host.innerHTML = schemaWarnings.length > 0
        ? banner('error', `Zusatzfelder nicht ladbar: ${schemaWarnings.join(' ')} Bitte erneut versuchen - die im Shop gepflegten Felder sind nicht verloren.`)
        : banner('info', 'Keine Gambio-Zusatzfelder vorhanden - im Gambio-Shop sind keine Zusatzfelder konfiguriert.');
      return;
    }
    // Pro Feld alle Sprach-Keys PLUS den alten sprachlosen Legacy-Key `f<id>` lesen - spiegelt
    // den Push-Reader (vario-channel-adapter.ts): Altbestände liegen sprachlos in f<id>.
    const allKeys = rows.flatMap((r) => [...r.cells.map((c) => c.attrKey), `f${r.fieldId}`]);
    const valRes = await callFn<{ values: Record<string, string> }>(ctx, 'gambio-article-additional', {
      action: 'get', articleId: args.varioArticleId, keys: allKeys,
    });
    values = valRes.values ?? {};
  } catch (e) {
    host.innerHTML = banner('error', `Zusatzfelder nicht ladbar: ${(e as Error).message}`);
    return;
  }

  // Per-Feld-Modus + Vario-Pfad aus dem geteilten Artikel-Override laden (field_group='additional').
  // Zusätzlich die GLOBALE Zusatzfeld-Regel (data.global) → lesbare „erbt:"-Zeile im Vorlage-Modus.
  const modeByFieldId = new Map<number, string>();
  const vpByFieldId = new Map<number, string>();
  const globalAddByFieldId = new Map<number, OverrideField>();
  let preservedScalar: OverrideField[] = [];
  try {
    const { data } = await ctx.supabase.rpc('gambio_get_article_mapping', { _listing_id: args.listingId });
    const ov = ((data as { override?: OverrideField[] } | null)?.override) ?? [];
    const gl = ((data as { global?: OverrideField[] } | null)?.global) ?? [];
    preservedScalar = ov.filter((f) => f.field_group !== 'additional');
    for (const f of ov) {
      if (f.field_group !== 'additional') continue;
      const m = f.gambio_field_key.match(/^f(\d+)$/);
      if (!m) continue;
      const fid = Number(m[1]);
      modeByFieldId.set(fid, f.source_type);
      if (f.source_type === 'vario_path' && f.vario_path) vpByFieldId.set(fid, f.vario_path);
    }
    for (const f of gl) {
      if (f.field_group !== 'additional') continue;
      const m = f.gambio_field_key.match(/^f(\d+)$/);
      if (m) globalAddByFieldId.set(Number(m[1]), f);
    }
  } catch { /* best-effort: ohne Override alle auf Vorlage */ }

  // Vario-Quellen dynamisch (gambio-mapping-sources) - dieselbe reiche Liste wie der Stammdaten-Tab.
  const afSources: FlatSource[] = await loadMappingSources(
    { supabaseUrl: ctx.supabaseUrl, session: ctx.session },
    { articleId: args.varioArticleId },
  );
  const srcByPath = new Map<string, FlatSource>(afSources.map((s) => [s.path, s]));

  // Effektiver Zell-Wert mit Legacy-Fallback (spiegelt vario-channel-adapter.ts): der sprachlose
  // Altwert `f<id>` füllt NUR die 'de'-Zelle und nur, wenn dort kein sprachspezifischer Wert liegt.
  const cellValue = (fieldId: number, lang: string, key: string): string => {
    const v = values[key];
    if (v) return v;
    if (lang === 'de') return values[`f${fieldId}`] ?? '';
    return '';
  };

  // Lesbare „erbt:"-Zeile für den Vorlage-Modus (analog Stammdaten-Tab): Klartext-Feldname +
  // aktueller Wert + Pfad. Ohne globale Regel (bzw. Modus „standard") exportiert der Artikel
  // seinen eigenen mehrsprachigen Zusatzfeld-Wert → diese Werte direkt anzeigen.
  const globalAddHint = (r: FieldRow): string => {
    const g = globalAddByFieldId.get(r.fieldId);
    if (!g || g.source_type === 'standard') {
      const parts = r.cells
        .map((c) => ({ lang: c.lang, v: cellValue(r.fieldId, c.lang, c.attrKey).trim() }))
        .filter((x) => x.v.length > 0)
        .map((x) => `${x.lang}: „${x.v}"`);
      return parts.length > 0
        ? `erbt: Zusatzfeld-Wert des Artikels - ${parts.join(', ')}`
        : 'erbt: Zusatzfeld-Wert des Artikels (leer)';
    }
    if (g.source_type === 'unmapped') return 'erbt: nicht gemappt';
    if (g.source_type === 'vario_path') {
      const path = g.vario_path ?? '';
      const src = path ? srcByPath.get(path) : undefined;
      const name = src?.label ?? (path || '—');
      const value = src?.example ? ` - ${src.example}` : '';
      const pathTail = path ? ` (${path})` : '';
      return `erbt: ${name}${value}${pathTail}`;
    }
    if (g.source_type === 'fixed_value') return `erbt: fester Wert „${typeof g.fixed_value === 'string' ? g.fixed_value : ''}"`;
    if (g.source_type === 'template') return `erbt: Formel ${g.template ?? ''}`;
    return 'erbt: —';
  };

  const cellHtml = (c: LangCell, fieldId: number): string => {
    const v = cellValue(fieldId, c.lang, c.attrKey);
    return `
      <div style="display:grid;grid-template-columns:44px 1fr;gap:8px;align-items:center;margin-top:4px;">
        <span class="v-text-muted" style="font-size:11px;text-transform:uppercase;">${esc(c.lang)}</span>
        <input class="v-input" data-attr-key="${esc(c.attrKey)}" type="text" placeholder="Wert (leer = kein Wert)" value="${esc(v)}" />
      </div>`;
  };

  // Grid und Kopfzeile teilen dieselbe Definition, damit sie nicht auseinanderlaufen.
  // Modus ZUERST (Betreiber-Ansage 21.08.2026) - er schaltet die Vario-Spalte um.
  const AF_GRID = '160px 1fr 170px';
  const afHeadHtml = (): string => `
    <div style="display:grid;grid-template-columns:${AF_GRID};gap:8px;padding:0 0 6px;border-bottom:1px solid var(--go-border,#e5e7eb);font-size:11px;font-weight:700;letter-spacing:.04em;text-transform:uppercase;color:var(--go-muted,#6b7280);">
      <div>Modus</div><div>Vario</div><div>Gambio</div>
    </div>`;

  const rowHtml = (r: FieldRow): string => {
    const mode = modeByFieldId.get(r.fieldId) ?? 'inherit';
    const vpSel = vpByFieldId.get(r.fieldId) ?? '';
    const opts = afSources.map((s) => {
      const suffix = s.example ? ` - ${s.example}` : '';
      return `<option value="${esc(s.path)}"${s.path === vpSel ? ' selected' : ''}>${esc(s.label + suffix)}</option>`;
    }).join('');
    // Layout identisch zum Stammdaten-Tab (list-mapping.ts):
    // Modus | Vario-Quelle/Ergebnis (1fr) | Gambio-Zusatzfeld.
    return `
    <div class="gaf-field" data-af-field="${r.fieldId}" style="display:grid;grid-template-columns:${AF_GRID};gap:8px;align-items:start;padding:8px 0;border-bottom:1px solid var(--go-border,#e5e7eb);">
      <div class="v-select"><select data-af-mode="${r.fieldId}">
        ${AF_MODES.map((m) => `<option value="${m.value}"${m.value === mode ? ' selected' : ''}>${m.label}</option>`).join('')}
      </select></div>
      <div>
        <div data-af-ctl="inherit" data-af-for="${r.fieldId}" hidden><span class="v-text-muted" style="font-size:12px;">${esc(globalAddHint(r))}</span></div>
        <div class="v-select" data-af-ctl="vario_path" data-af-for="${r.fieldId}" hidden><select data-af-vp="${r.fieldId}">
          <option value="">— Vario-Feld wählen —</option>${opts}
        </select></div>
        <div data-af-ctl="unmapped" data-af-for="${r.fieldId}" hidden><span class="v-text-muted" style="font-size:12px;">Wird für diesen Artikel nicht übertragen.</span></div>
        <div data-af-ctl="fixed_value" data-af-for="${r.fieldId}" hidden>${r.cells.map((c) => cellHtml(c, r.fieldId)).join('')}</div>
      </div>
      <div><strong>${esc(r.baseLabel)}</strong><div class="v-text-muted" style="font-size:11px;">Gambio-Zusatzfeld</div></div>
    </div>`;
  };

  // Sprachset aus dem geladenen Schema ableiten (de zuerst) - Fallback, falls (noch) keine Felder existieren.
  const langs = [...new Set(rows.flatMap((r) => r.cells.map((c) => c.lang)))].sort(sortLangs);
  const newFieldLangs = langs.length > 0 ? langs : ['de', 'en'];

  const newFieldRowHtml = (lang: string, isFirst: boolean): string => `
    <div style="display:grid;grid-template-columns:44px 1fr;gap:8px;align-items:center;margin-top:4px;">
      <span class="v-text-muted" style="font-size:11px;text-transform:uppercase;">${esc(lang)}</span>
      <input class="v-input" data-new-field-lang="${esc(lang)}" type="text"
        placeholder="${isFirst ? 'Name (Pflicht)' : 'Name (optional)'}" />
    </div>`;

  host.innerHTML = `
    <div class="v-pa-md">
      <div class="v-row v-row-sm" style="justify-content:space-between;align-items:center;margin-bottom:8px;gap:10px;">
        <h4 class="klst-detail-section" style="margin:0;">Zusatzfelder</h4>
        <button type="button" class="v-button v-button--save" id="gaf-save">
          <span class="v-icon fa-regular fa-floppy-disk"></span> Speichern
        </button>
      </div>
      <div style="margin-bottom:10px;">
        <button type="button" class="v-button v-button--secondary" id="gaf-new-toggle">
          <span class="v-icon fa-regular fa-plus"></span> Neues Zusatzfeld
        </button>
      </div>
      <div id="gaf-new-form" class="gaf-field" style="display:none;padding:8px 0 12px;border-bottom:1px solid var(--go-border,#e5e7eb);margin-bottom:8px;">
        <div><strong>Neues Zusatzfeld</strong>
          <span class="v-text-muted" style="font-size:11px;margin-left:6px;">Name je Sprache</span></div>
        ${newFieldLangs.map((l, i) => newFieldRowHtml(l, i === 0)).join('')}
        <div class="v-row v-row-sm" style="gap:8px;margin-top:8px;">
          <button type="button" class="v-button v-button--primary" id="gaf-new-create">
            <span class="v-icon fa-regular fa-check"></span> Anlegen
          </button>
          <button type="button" class="v-button v-button--secondary" id="gaf-new-cancel">Abbrechen</button>
        </div>
      </div>
      <p class="v-text-muted" style="font-size:12px;margin:0 0 10px;">
        Pro Zusatzfeld ein Modus. „Fester Wert" nutzt die mehrsprachigen Werte dieses Artikels
        (werden ins Vario-Zusatzfeld geschrieben; der Sync trägt sie nach Gambio). „Vorlage" folgt
        dem globalen Zusatzfeld-Mapping.
      </p>
      <div id="gaf-rows">${rows.length ? afHeadHtml() : ''}${rows.map(rowHtml).join('')}</div>
    </div>`;

  // Frisch gerenderter Tab = keine ungespeicherten Eingaben (Tab-Wechsel-Guard).
  clearTabDirty(host);

  // Modus-Umschaltung: je Feld nur den passenden Steuer-Block zeigen.
  const syncAf = (fieldId: string, mode: string): void => {
    host.querySelectorAll<HTMLElement>(`[data-af-for="${CSS.escape(fieldId)}"]`).forEach((el) => {
      const show = el.getAttribute('data-af-ctl') === mode;
      // Inline-display: `.v-select { display:inline-flex }` schlaegt sonst `[hidden]` → das
      // „Vario-Feld"-Dropdown blieb im Vorlage-/Fester-Wert-Modus faelschlich sichtbar.
      el.hidden = !show;
      el.style.display = show ? '' : 'none';
    });
  };
  host.querySelectorAll<HTMLSelectElement>('[data-af-mode]').forEach((sel) => {
    const fid = sel.getAttribute('data-af-mode')!;
    sel.addEventListener('change', () => syncAf(fid, sel.value));
    syncAf(fid, sel.value);
  });

  host.querySelector<HTMLButtonElement>('#gaf-save')?.addEventListener('click', async () => {
    // 1) Werte (bei fixed_value relevant; wir schreiben alle vorhandenen EAV-Zellen).
    const out: Record<string, string> = {};
    host.querySelectorAll<HTMLInputElement>('[data-attr-key]').forEach((inp) => {
      const key = inp.dataset['attrKey'];
      if (key) out[key] = inp.value;
    });
    // 2) Modi in den geteilten Override. Die 'product_data_basic'-Zeilen des Stammdaten-Tabs
    //    IMMER frisch nachladen (read-merge-write), nicht den Mount-Snapshot - sonst Clobbering.
    let curScalar: OverrideField[] = preservedScalar;
    try {
      const { data: curData } = await ctx.supabase.rpc('gambio_get_article_mapping', { _listing_id: args.listingId });
      curScalar = (((curData as { override?: OverrideField[] } | null)?.override) ?? []).filter((f) => f.field_group !== 'additional');
    } catch { /* Fallback: Mount-Snapshot */ }
    const fields: OverrideField[] = [...curScalar];
    host.querySelectorAll<HTMLElement>('[data-af-field]').forEach((row) => {
      const fid = Number(row.getAttribute('data-af-field'));
      const mode = row.querySelector<HTMLSelectElement>('[data-af-mode]')!.value;
      if (mode === 'inherit') return;                       // Vorlage → nicht speichern
      const entry: OverrideField = { gambio_field_key: `f${fid}`, field_group: 'additional', source_type: mode };
      if (mode === 'vario_path') entry.vario_path = row.querySelector<HTMLSelectElement>('[data-af-vp]')?.value || null;
      // fixed_value: Wert liegt in EAV (out), NICHT im Override.
      fields.push(entry);
    });
    try {
      await callFn(ctx, 'gambio-article-additional', { action: 'save', articleId: args.varioArticleId, values: out });
      const { error: mErr } = await ctx.supabase.rpc('gambio_save_article_mapping', { _listing_id: args.listingId, _fields: fields });
      if (mErr) throw new Error(mErr.message);
      showToast('Zusatzfelder gespeichert.', 'success');
      clearTabDirty(host);
    } catch (e) {
      showToast(`Speichern fehlgeschlagen: ${(e as Error).message}`, 'error');
    }
  });

  const newForm = host.querySelector<HTMLDivElement>('#gaf-new-form');
  host.querySelector<HTMLButtonElement>('#gaf-new-toggle')?.addEventListener('click', () => {
    if (newForm) newForm.style.display = newForm.style.display === 'none' ? 'block' : 'none';
  });
  host.querySelector<HTMLButtonElement>('#gaf-new-cancel')?.addEventListener('click', () => {
    if (newForm) newForm.style.display = 'none';
  });
  host.querySelector<HTMLButtonElement>('#gaf-new-create')?.addEventListener('click', async () => {
    const names: Record<string, string> = {};
    newForm?.querySelectorAll<HTMLInputElement>('[data-new-field-lang]').forEach((inp) => {
      const lang = inp.dataset['newFieldLang'];
      const val = inp.value.trim();
      if (lang && val.length > 0) names[lang] = val;
    });
    const firstLang = newFieldLangs[0];
    if (!firstLang || !names[firstLang]) {
      showToast(`Name (${firstLang ?? 'erste Sprache'}) ist erforderlich.`, 'error');
      return;
    }
    try {
      await callFn(ctx, 'gambio-article-additional-catalog', {
        action: 'create', storefront: args.storefront, names,
      });
      showToast('Zusatzfeld angelegt.', 'success');
      await renderListAdditionalFields(host, ctx, args);
    } catch (e) {
      showToast(`Anlegen fehlgeschlagen: ${(e as Error).message}`, 'error');
    }
  });
}
